/**
 * Utilitaire pour la compression d'images
 */

const DEFAULT_OPTIONS = {
  maxFileSize: 2 * 1024 * 1024, // 2MB en bytes
  compressionQuality: 0.8, // Qualité de compression (0.1 à 1.0)
  maxImageDimension: 1920, // Dimension maximale pour les images
};

/**
 * Vérifie si un fichier est une image
 * @param {File} file - Le fichier à vérifier
 * @returns {boolean}
 */
export function isImageFile(file) {
  return file.type.startsWith("image/");
}

/**
 * Compresse une image
 * @param {File} file - Le fichier image à compresser
 * @param {Object} options - Options de compression
 * @returns {Promise<File>}
 */
export function compressImage(file, options = {}) {
  const config = { ...DEFAULT_OPTIONS, ...options };

  return new Promise((resolve) => {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();

    img.onload = () => {
      // Calculer les nouvelles dimensions en gardant le ratio
      let { width, height } = img;
      const ratio = Math.min(
        config.maxImageDimension / width,
        config.maxImageDimension / height
      );

      if (ratio < 1) {
        width *= ratio;
        height *= ratio;
      }

      canvas.width = width;
      canvas.height = height;

      // Dessiner l'image redimensionnée
      ctx.drawImage(img, 0, 0, width, height);

      // Convertir en blob avec compression
      canvas.toBlob(
        (blob) => {
          // Créer un nouveau File object avec le même nom
          const compressedFile = new File([blob], file.name, {
            type: file.type,
            lastModified: Date.now(),
          });
          resolve(compressedFile);
        },
        file.type,
        config.compressionQuality
      );
    };

    img.src = URL.createObjectURL(file);
  });
}

/**
 * Traite un fichier en le compressant si nécessaire
 * @param {File} file - Le fichier à traiter
 * @param {Object} options - Options de compression
 * @returns {Promise<File>}
 */
export async function processFile(file, options = {}) {
  const config = { ...DEFAULT_OPTIONS, ...options };

  // Si c'est une image et qu'elle dépasse la taille limite
  if (isImageFile(file) && file.size > config.maxFileSize) {
    try {
      const compressedFile = await compressImage(file, options);
      console.log(
        `Image compressée: ${file.name} (${(file.size / 1024 / 1024).toFixed(
          2
        )}MB → ${(compressedFile.size / 1024 / 1024).toFixed(2)}MB)`
      );
      return compressedFile;
    } catch (error) {
      console.error("Erreur lors de la compression:", error);
      return file; // Retourner le fichier original en cas d'erreur
    }
  }
  return file; // Retourner le fichier sans modification
}

/**
 * Force le téléchargement d'un fichier distant via fetch (contourne la restriction cross-origin du `download`)
 */
export async function downloadFile(url, filename) {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename || url.split('/').pop();
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  } catch (error) {
    console.error('Erreur lors du téléchargement:', error);
    // Fallback : ouvrir dans un nouvel onglet
    window.open(url, '_blank');
  }
}
